// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { Content } from 'components';
import { Button } from '@stormideas/react-sdk';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';

import './PrivacyPolicy.scss';

const handleBackClick = history => () => {
  if (history.length > 1) {
    history.goBack();
    return;
  }

  history.push('/dashboard');
};

const PrivacyPolicy = ({ history }) => (
  <Content
    title="Privacy Policy"
    className="privacy-policy-page"
    topRightContent={
      <Button
        className="privacy-policy__back-button"
        color="secondary"
        onClick={handleBackClick(history)}
        type="button"
      >
        <FontAwesomeIcon icon={faArrowLeft} />
        Back
      </Button>
    }
  >
    <div className="privacy-policy">
      <p>
        <span>
          Thank you for visiting an online service that posts a link to this
          Privacy Policy and is owned or operated by StormKit. This Privacy
          Policy explains how StormKit collects, uses, and shares information
          about you, including the choices StormKit offers with respect to that
          information. It applies to your use of any service, regardless of how
          you access or use it.
        </span>
      </p>

      <p>
        <span>
          By visiting or otherwise using the service, you consent to StormKit's
          collection, use, and disclosure of information, and to the other
          activities described in this Privacy Policy. If you do not agree and
          consent, please discontinue use of the service.
        </span>
      </p>

      <p>
        <span>
          StormKit is committed to ensuring that your privacy is protected.
          Should we ask you to provide certain information by which you can be
          identified when using this service, you can be assured that it will
          only be used in accordance with this policy.
        </span>
      </p>

      <p>
        <span>
          StormKit may change this policy from time to time by updating this
          page. You should check this page from time to time to ensure that you
          are happy with any changes. This policy is effective from 12 June
          2026.
        </span>
      </p>

      <h1>
        <span>What we may collect and what we do with this information</span>
      </h1>

      <h2>
        <span>Contact Information</span>
      </h2>

      <p>
        <span>When: When we create an account for you.</span>
      </p>

      <p>
        <span>What: Your name and email address.</span>
      </p>

      <p>
        <span>Why: To allow you to log in to use the service.</span>
      </p>

      <h2>
        <span>Meta, Facebook, and Instagram Platform Data</span>
      </h2>

      <p>
        <span>
          When: When you connect a Facebook page, a connected Instagram account,
          or another Meta integration to StormKit.
        </span>
      </p>

      <p>
        <span>
          What: Data made available to StormKit through Meta permissions. This
          can include page and account identifiers, page and account names or
          usernames, account authorisation tokens, connected Instagram account
          identifiers and usernames, page posts, tagged posts, comments,
          replies, comment author names, message or comment text, post and
          comment links, creation times, comment like counts, Instagram insight
          metrics such as follower counts and profile views, and image or video
          URLs used for social moderation, reporting, or operational support.
        </span>
      </p>

      <p>
        <span>
          Why: To authenticate connected accounts, list pages available to the
          authorised user, keep the integration working, fetch content that
          needs moderation, show moderation items for review, take moderation
          actions requested by authorised StormKit users, produce connected
          account reporting, identify top comments, and provide operational
          support.
        </span>
      </p>

      <p>
        <span>
          We do not sell Meta Platform Data. StormKit is designed to remove old
          social moderation post and media records after the operational review
          window unless we need to retain them for active moderation,
          troubleshooting, security, audit, or legal reasons.
        </span>
      </p>

      <p>
        <span>
          Where we use service providers for hosting, moderation workflows,
          reporting, storage, support, or diagnostics, those providers process
          Meta Platform Data on StormKit's behalf and not for their own
          independent sale or use.
        </span>
      </p>

      <h2>
        <span>YouTube and Google API Data</span>
      </h2>

      <p>
        <span>
          When: When you connect a YouTube account or another Google API
          integration to StormKit.
        </span>
      </p>

      <p>
        <span>
          What: Data made available to StormKit through Google OAuth permissions
          and YouTube API Services. This can include YouTube channel
          identifiers, channel names, video identifiers, video metadata, comment
          and moderation data, YouTube Analytics metrics, Google user
          identifiers, and OAuth access and refresh tokens needed to keep the
          connected account working.
        </span>
      </p>

      <p>
        <span>
          Why: To authenticate connected YouTube accounts, fetch channel, video,
          comment, and analytics data requested by authorised StormKit users,
          provide YouTube reporting and moderation features, keep scheduled
          reporting jobs working, and provide operational support.
        </span>
      </p>

      <p>
        <span>
          StormKit uses YouTube API Services and Google APIs only to provide and
          improve StormKit reporting, moderation, account-connection, and
          operational support features. StormKit does not sell YouTube or Google
          API data.
        </span>
      </p>

      <p>
        <span>
          StormKit's use and transfer to any other app of information received
          from Google APIs will adhere to the{' '}
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            rel="noopener noreferrer"
            target="_blank"
          >
            Google API Services User Data Policy,
          </a>{' '}
          including the Limited Use requirements. Use of YouTube API Services is
          subject to the{' '}
          <a
            href="https://developers.google.com/youtube/terms/api-services-terms-of-service"
            rel="noopener noreferrer"
            target="_blank"
          >
            YouTube API Services Terms of Service.
          </a>{' '}
          Google's Privacy Policy is available at{' '}
          <a
            href="https://policies.google.com/privacy"
            rel="noopener noreferrer"
            target="_blank"
          >
            https://policies.google.com/privacy.
          </a>
        </span>
      </p>

      <p>
        <span>
          You may revoke StormKit's access to your Google Account at any time
          through your Google Account permissions page at{' '}
          <a
            href="https://myaccount.google.com/permissions"
            rel="noopener noreferrer"
            target="_blank"
          >
            https://myaccount.google.com/permissions.
          </a>{' '}
          You may also request that we disconnect a connected YouTube account
          and delete or irreversibly redact StormKit-controlled YouTube or
          Google API data as described in the data deletion request process
          below.
        </span>
      </p>

      <h2>
        <span>
          Information that your browser automatically sends whenever you visit a
          website
        </span>
      </h2>

      <p>
        <span>When: When you visit this service.</span>
      </p>

      <p>
        <span>
          What: This data may include information such as your IP address,
          browser type or the domain from which you are visiting, the web pages
          you visit, the search terms you use, the location of your ISP, and any
          advertisements on which you click.
        </span>
      </p>

      <p>
        <span>Why:</span>
      </p>

      <p>
        <span>
          <span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
        </span>
        <span>To monitor the use of this service.</span>
      </p>

      <p>
        <span>
          <span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
        </span>
        <span>For this service's technical administration.</span>
      </p>

      <p>
        <span>
          <span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
        </span>
        <span>
          NOT to associate your IP address with any other personally
          identifiable information to identify you personally.
        </span>
      </p>

      <p>
        <span>
          We may disclose any of the above information for special cases where
          we have reason to believe that doing so is necessary to identify,
          contact or bring legal action against someone who may be causing
          injury or interference with our rights, property, our customers or
          anyone else who could be harmed by such activities. We may also
          disclose information we collect when we believe, in good faith, that
          the law requires it.
        </span>
      </p>

      <h1>
        <span>Security</span>
      </h1>

      <p>
        <span>
          We are committed to ensuring that your information is secure. In order
          to prevent unauthorised access or disclosure, we have put in place
          suitable physical, electronic and managerial procedures to safeguard
          and secure the information we collect online.
        </span>
      </p>

      <p>
        <span>
          The data we collect is stored with an industry-leading cloud provider
          within the US. All of our data is encrypted while it is at rest.
        </span>
      </p>

      <p>
        <span>Additionally:</span>
      </p>

      <p>
        <span>
          <span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
        </span>
        <span>
          For data in transit, industry-standard transport protocols between
          user devices and our data centers are used. These same protocols are
          used within the data centers themselves.
        </span>
      </p>

      <p>
        <span>
          <span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
        </span>
        <span>
          Detection and mitigation techniques are used to protect against DDoS
          attacks.
        </span>
      </p>

      <p>
        <span>
          <span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
        </span>
        <span>Servers have antimalware software installed.</span>
      </p>

      <p>
        <span>
          <span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
        </span>
        <span>Regular penetration testing is conducted.</span>
      </p>

      <p>
        <span>
          <span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
        </span>
        <span>
          Our cloud provider has a global incident response team which works
          around the clock to mitigate the effects of any attacks. These are
          backed by centers of excellence that fight digital crime, respond to
          security incidents and vulnerabilities, and combat malware.
        </span>
      </p>

      <h1>
        <span>How we use cookies</span>
      </h1>

      <p>
        <span>
          Cookies are small pieces of information sent by a web server to a web
          browser which allows the server to uniquely identify the browser on
          each page.
        </span>
      </p>

      <p>
        <span>
          StormKit uses Facebook, Twitter, Google, and other provider login or
          account-connection features. These providers may store cookies that
          are required for us to connect to your Facebook page, Twitter profile,
          YouTube account, or other connected account. Facebook, Twitter, and
          Google each have their own cookie or privacy policies, which can be
          viewed at https://www.facebook.com/policies/cookies/,
          https://twitter.com/privacy, and https://policies.google.com/privacy
          respectively.
        </span>
      </p>

      <p>
        <span>
          A cookie in no way gives us access to your computer or any information
          about you, other than the data you choose to share with us.
        </span>
      </p>

      <p>
        <span>
          You can choose to accept or decline cookies. Most web browsers
          automatically accept cookies, but you can usually modify your browser
          setting to decline cookies if you prefer. This may prevent you from
          taking full advantage of the service.
        </span>
      </p>

      <h1>
        <span>Controlling your personal information</span>
      </h1>

      <p>
        <span>
          Please contact us at hello@stormideas.com if you want to exercise any
          of these rights:
        </span>
      </p>

      <p>
        <span>
          <span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
        </span>
        <span>
          You wish to request details of personal information which we hold
          about you.
        </span>
      </p>

      <p>
        <span>
          <span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
        </span>
        <span>
          You wish for us to restrict the collection, processing or use of your
          personal information.
        </span>
      </p>

      <p>
        <span>
          <span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
        </span>
        <span>
          You wish for us to stop the collection, processing or use of your
          personal information.
        </span>
      </p>

      <p>
        <span>
          <span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
        </span>
        <span>
          You wish for us to delete or destroy any record of your personal
          information.
        </span>
      </p>

      <p>
        <span>
          <span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
        </span>
        <span>
          You wish to request deletion of data associated with your use of
          StormKit, including Meta, Facebook, Instagram, YouTube, or Google API
          data controlled by StormKit.
        </span>
      </p>

      <p>
        <span>
          <span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
        </span>
        <span>
          You wish for us to transfer your personal information elsewhere.
        </span>
      </p>

      <p>
        <span>
          <span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
        </span>
        <span>
          You believe any information we are holding about you is incorrect or
          incomplete.
        </span>
      </p>

      <p>
        <span>
          <span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
        </span>
        <span>We will respond to your query within 30 days.</span>
      </p>

      <p>
        <span>
          The data deletion request process described above is available to all
          users who can access StormKit. To make a data deletion request, email
          hello@stormideas.com with the subject "StormKit data deletion request"
          and include enough information for us to identify the relevant
          StormKit account, Facebook page, Instagram account, YouTube channel,
          Google account, or other connected account. We will verify the
          request, remove or irreversibly redact the relevant
          StormKit-controlled data, and confirm the outcome or explain why
          specific data must be retained for legal, security, audit,
          fraud-prevention, or troubleshooting reasons. You may also request
          that we disconnect a connected Facebook page, Instagram account,
          YouTube channel, Google account, or other connected account and remove
          related authorisation tokens. Where service providers process the
          relevant data on StormKit's behalf, we will make reasonable efforts to
          ensure they delete the data as part of the request. Data held directly
          by Meta, Facebook, Instagram, Google, or YouTube must be managed
          through those services' account and privacy tools.
        </span>
      </p>
    </div>
  </Content>
);

PrivacyPolicy.propTypes = {
  history: PropTypes.shape({
    goBack: PropTypes.func,
    length: PropTypes.number,
    push: PropTypes.func,
  }).isRequired,
};

export default withRouter(PrivacyPolicy);
